import { memo, useMemo } from 'react';
import { Link } from 'react-router';
import {
  FLOW_GROUPS,
  GROUP_DROP_STATUS,
  flowGroup,
  groupByFlow,
  statusMark,
  type FlowGroup,
} from '@shared/domain/flow.ts';
import { dayTotals, formatHoursValue, secondsToHours } from '@shared/domain/hours.ts';
import { localDate } from '@shared/domain/time.ts';
import { store, useApp } from '../state/store.ts';
import { formatElapsed, formatFechas } from '../lib/format.ts';
import type { CardListItem, TimeEntry } from '../lib/types.ts';
import { HoursButton, useNow, usePullToRefresh, useWideLayout } from '../components/common.tsx';
import { Kanban } from '../components/Kanban.tsx';

const FOUR_HOURS = 4 * 3600;

interface TaskProps {
  card: CardListItem;
  todaySeconds: number;
  running: boolean;
}

// Memoizada y sin suscribirse al store, como las filas de Todas las tarjetas. Sin el <li>: la
// lista y el Kanban ponen el suyo.
const TaskContent = memo(function TaskContent({ card, todaySeconds, running }: TaskProps) {
  const name = card.name || '(sin nombre)';
  const mark = statusMark(card.status);
  const fechas = formatFechas(card.fechas);
  return (
    <>
      <Link
        to={`/tarjeta/${card.id}`}
        className={`card-item task-item${running ? ' card-item-running' : ''}`}
      >
        <span className="card-title">
          {name}
          {mark && (
            <span
              className={`chip chip-mark chip-mark-${mark === 'Urgente' ? 'urgente' : 'correccion'}`}
            >
              {mark}
            </span>
          )}
        </span>
        {card.summary && <span className="task-summary">{card.summary}</span>}
        {(fechas || todaySeconds > 0) && (
          <span className="card-foot muted small">
            <span>{fechas}</span>
            {todaySeconds > 0 && (
              <span data-testid="task-today">
                Hoy {formatHoursValue(secondsToHours(todaySeconds))}
              </span>
            )}
          </span>
        )}
      </Link>
      <HoursButton cardId={card.id} name={name} running={running} />
    </>
  );
});

function TaskItem(props: TaskProps) {
  return (
    <li className="card-row">
      <TaskContent {...props} />
    </li>
  );
}

/** Franja de arriba: la tarea con el cronómetro en curso (sea o no de Mis tareas). */
function WorkingOn({
  entry,
  card,
  todaySeconds,
}: {
  entry: TimeEntry;
  card: CardListItem | undefined;
  todaySeconds: number;
}) {
  const now = useNow(1000);
  const elapsed = (now.getTime() - Date.parse(entry.started_at)) / 1000;
  const overFour = elapsed >= FOUR_HOURS || Boolean(entry.warned_at);
  return (
    <section className="working-on" aria-label="Trabajando en" data-testid="working-on">
      <h2>Trabajando en</h2>
      {card ? (
        <ul className="card-list">
          <TaskItem card={card} todaySeconds={todaySeconds} running />
        </ul>
      ) : (
        <p>
          <Link to={`/tarjeta/${entry.card_id}`}>Tarjeta en curso</Link>
        </p>
      )}
      <div className="working-on-timer" role="status">
        <span className="timer-dot" aria-hidden="true" />
        <span className="timer-elapsed">{formatElapsed(elapsed)}</span>
        <button
          type="button"
          className="button button-small"
          onClick={() => void store.stopTimer()}
        >
          Detener
        </button>
      </div>
      {overFour && (
        <p className="timer-warning">
          El cronómetro lleva más de 4 horas. Se cerrará solo a las 8 horas y quedará para revisión.
        </p>
      )}
    </section>
  );
}

export function MyTasksPage() {
  const { cards, details, profile, openEntry, myLogs, syncing } = useApp();
  const me = profile?.notion_user_id ?? null;
  const today = localDate(useNow(60_000));
  const runningId = openEntry?.card_id ?? null;
  const { handlers, hint } = usePullToRefresh();
  const wide = useWideLayout();

  const mine = useMemo(
    () => cards.filter((c) => !c.is_closed && me !== null && c.miembros.includes(me)),
    [cards, me],
  );
  const sections = useMemo(
    () => groupByFlow(mine.filter((c) => c.id !== runningId)),
    [mine, runningId],
  );
  const day = useMemo(() => dayTotals(myLogs, today), [myLogs, today]);
  const todayByCard = useMemo(() => new Map(day.rows.map((l) => [l.card_id, l.seconds])), [day]);

  // La tarea en curso cuenta si es de Mis tareas (está en la franja, no en su grupo).
  const count =
    sections.reduce((n, s) => n + s.cards.length, 0) +
    (mine.some((c) => c.id === runningId && flowGroup(c.status) !== null) ? 1 : 0);
  const running = runningId
    ? (cards.find((c) => c.id === runningId) ?? details[runningId])
    : undefined;

  return (
    <div {...handlers}>
      {hint}
      <div className="page-head">
        <h1>Mis tareas</h1>
        <Link to="/hoy" className="today-link" data-testid="today-link">
          {day.seconds > 0
            ? `Hoy llevas ${formatHoursValue(secondsToHours(day.seconds))}`
            : 'Hoy: sin horas registradas'}
        </Link>
      </div>
      <p className="muted small" data-testid="card-count">
        {count === 1 ? '1 tarea' : `${count} tareas`}
        {syncing ? ' · actualizando…' : ''}
      </p>

      {openEntry && (
        <WorkingOn
          entry={openEntry}
          card={running}
          todaySeconds={todayByCard.get(openEntry.card_id) ?? 0}
        />
      )}

      {count === 0 && (
        <p className="empty">
          No tienes tareas asignadas. <Link to="/todas">Ver todas las tarjetas</Link>
        </p>
      )}

      {wide ? (
        <Kanban
          label="Mis tareas por grupo"
          columns={FLOW_GROUPS.map((g) => ({
            key: g.key,
            label: g.label,
            folded: g.folded,
            tone: `flow-${g.key}`,
            cards: sections.find((s) => s.key === g.key)?.cards ?? [],
          }))}
          cardName={(card) => card.name || '(sin nombre)'}
          renderCard={(card) => (
            <TaskContent card={card} todaySeconds={todayByCard.get(card.id) ?? 0} running={false} />
          )}
          onMove={(card, to) =>
            void store.updateCard(card.id, { status: GROUP_DROP_STATUS[to as FlowGroup] })
          }
        />
      ) : (
        sections.map((section) => (
          <section
            key={section.key}
            className={`flow-section flow-${section.key}`}
            aria-label={section.label}
          >
            <details open={!section.folded}>
              <summary>
                <h2>
                  {section.label} <span className="count">{section.cards.length}</span>
                </h2>
              </summary>
              <ul className="card-list">
                {section.cards.map((card) => (
                  <TaskItem
                    key={card.id}
                    card={card}
                    todaySeconds={todayByCard.get(card.id) ?? 0}
                    running={false}
                  />
                ))}
              </ul>
            </details>
          </section>
        ))
      )}
    </div>
  );
}
